import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Mail, RefreshCw, Database } from 'lucide-react';
import { PageHeader, Tabs, TabList, Tab, TabPanel, useConfirm, describeError } from '@/components/primitives';
import { API, auth, useBreachData } from '@/pages/breach-monitor/api';
import BreachSummaryCards from '@/pages/breach-monitor/BreachSummaryCards';
import DomainList from '@/pages/breach-monitor/DomainList';
import EmailList from '@/pages/breach-monitor/EmailList';
import BreachResultsList from '@/pages/breach-monitor/BreachResultsList';
import RetrievePasswordDialog from '@/pages/breach-monitor/RetrievePasswordDialog';
import '@/styles/workspace.css';

const TABS = ['results', 'emails', 'domains'];

const errorText = (err, fallback) => err?.response?.data?.detail && typeof err.response.data.detail === 'string'
  ? err.response.data.detail
  : describeError(err) || fallback;

const BreachMonitor = () => {
  const { domains, emails, results, summary, schedule, reload } = useBreachData();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'results';
  const setTab = (t) => setParams((p) => {
    const next = new URLSearchParams(p);
    if (t === 'results') next.delete('tab'); else next.set('tab', t);
    return next;
  }, { replace: true });
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [checking, setChecking] = useState({});
  const [verifyChecks, setVerifyChecks] = useState({});
  const [revealFor, setRevealFor] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  const s = summary.data;
  const counts = {
    domains: s?.total_domains ?? domains.data?.domains?.length,
    emails: s?.total_emails ?? emails.data?.total,
    results: s?.total_breaches ?? results.data?.stats?.total,
  };

  const refresh = async () => {
    setRefreshing(true);
    await reload();
    setRefreshing(false);
  };

  const addDomain = async (domain, autoEnumerate) => {
    try {
      const res = await axios.post(`${API}/domains/register`, { domain, auto_enumerate: autoEnumerate }, auth());
      const n = res.data.emails_added || 0;
      toast.success(`${res.data.domain?.domain || domain} added`, {
        description: n ? `${n} role addresses will be checked. Verify the domain to unlock password details.` : 'Verify the domain to unlock password details.',
      });
      reload();
    } catch (err) {
      toast.error(errorText(err, 'Could not add the domain'));
      throw err;
    }
  };

  const verifyDomain = async (d) => {
    setVerifyChecks((prev) => ({ ...prev, [d.id]: { status: 'pending' } }));
    try {
      const res = await axios.post(`${API}/domains/${d.id}/verify`, {}, auth());
      if (res.data.verified) {
        toast.success(`${res.data.domain} verified`, { description: 'Password details for its addresses are now available.' });
        setVerifyChecks((prev) => { const next = { ...prev }; delete next[d.id]; return next; });
        reload();
      } else {
        setVerifyChecks((prev) => ({ ...prev, [d.id]: { status: 'not_found', at: new Date() } }));
      }
    } catch (err) {
      setVerifyChecks((prev) => ({ ...prev, [d.id]: { status: 'error', message: errorText(err, 'Verification failed') } }));
    }
  };

  const removeDomain = async (d) => {
    const ok = await confirm({
      title: `Stop monitoring ${d.domain}?`,
      description: 'Its auto-discovered addresses stop being checked. Addresses you added by hand and existing breach results are kept. You can add the domain again later, but it will need to be verified again.',
      confirmLabel: 'Stop monitoring',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/domains/${d.id}`, auth());
      toast.success(`${d.domain} is no longer monitored`);
      if (selectedDomain === d.domain) setSelectedDomain(null);
      reload();
    } catch (err) {
      toast.error(errorText(err, 'Could not remove the domain'));
    }
  };

  const addEmail = async (email) => {
    try {
      await axios.post(`${API}/emails/register`, { email }, auth());
      toast.success(`${email} added`, { description: 'Use Check to run the first breach check now.' });
      reload();
    } catch (err) {
      toast.error(errorText(err, 'Could not add the email'));
      throw err;
    }
  };

  const removeEmail = async (e) => {
    const ok = await confirm({
      title: `Stop monitoring ${e.email}?`,
      description: 'It will no longer be checked for new breaches. Breach results already found for it are kept.',
      confirmLabel: 'Stop monitoring',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/emails/${e.id}`, auth());
      toast.success(`${e.email} is no longer monitored`);
      reload();
    } catch (err) {
      toast.error(errorText(err, 'Could not remove the email'));
    }
  };

  const checkEmail = async (e) => {
    setChecking((prev) => ({ ...prev, [e.id]: true }));
    try {
      const res = await axios.post(`${API}/emails/${e.id}/check`, {}, auth());
      const r = res.data;
      const incomplete = r.hibp_error || r.dehashed_error;
      if (r.total_breaches > 0) {
        toast.warning(`${r.email}: ${r.total_breaches} breach${r.total_breaches === 1 ? '' : 'es'} found`, {
          description: `Severity ${String(r.severity).toUpperCase()}${r.has_password_exposed ? ' · a password was exposed' : ''}${incomplete ? ' · one source did not respond, so this may be incomplete' : ''}`,
        });
      } else if (incomplete) {
        toast.warning(`${r.email}: check incomplete`, { description: 'A breach source did not respond, so "no breaches" could not be confirmed. Try again later.' });
      } else {
        toast.success(`${r.email}: no breaches found`);
      }
      reload();
    } catch (err) {
      toast.error(errorText(err, 'Breach check failed'));
    } finally {
      setChecking((prev) => ({ ...prev, [e.id]: false }));
    }
  };

  const acknowledge = async (ids) => {
    const settled = await Promise.allSettled(ids.map((id) => axios.put(`${API}/breach-results/${id}/acknowledge`, {}, auth())));
    const failed = settled.filter((r) => r.status === 'rejected').length;
    const done = ids.length - failed;
    if (done) toast.success(done === 1 ? 'Breach acknowledged' : `${done} breaches acknowledged`);
    if (failed) toast.error(`${failed} could not be acknowledged`, { description: describeError(settled.find((r) => r.status === 'rejected').reason) });
    reload();
  };

  const viewEmails = (domain) => { setSelectedDomain(domain); setTab('emails'); };

  return (
    <div className="pg cr" data-testid="credentials-page">
      <PageHeader
        title="Credentials"
        subtitle="Employee addresses found in known breaches, for the domains and addresses you monitor. Passwords stay hidden until you prove you own the domain."
        actions={
          <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={refreshing ? 'is-spinner' : undefined} aria-hidden="true" />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      <BreachSummaryCards summary={summary} schedule={schedule} onRetry={reload} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabList label="Credential sections">
          <Tab value="results" icon={Database} count={counts.results}>Breach results</Tab>
          <Tab value="emails" icon={Mail} count={counts.emails}>Addresses</Tab>
          <Tab value="domains" icon={Globe} count={counts.domains}>Domains</Tab>
        </TabList>
        <TabPanel value="domains" style={{ paddingTop: 'var(--space-5)' }}>
          <DomainList
            domains={domains}
            verifyChecks={verifyChecks}
            onRetry={reload}
            onAdd={addDomain}
            onVerify={verifyDomain}
            onRemove={removeDomain}
            onViewEmails={viewEmails}
          />
        </TabPanel>
        <TabPanel value="emails" style={{ paddingTop: 'var(--space-5)' }}>
          <EmailList
            emails={emails}
            selectedDomain={selectedDomain}
            checking={checking}
            onRetry={reload}
            onAdd={addEmail}
            onCheck={checkEmail}
            onRemove={removeEmail}
            onClearFilter={() => setSelectedDomain(null)}
          />
        </TabPanel>
        <TabPanel value="results" style={{ paddingTop: 'var(--space-5)' }}>
          <BreachResultsList
            results={results}
            domains={domains}
            onRetry={reload}
            onRetrieve={setRevealFor}
            onAcknowledge={acknowledge}
            onGoToDomains={() => setTab('domains')}
            onGoToEmails={() => setTab('emails')}
          />
        </TabPanel>
      </Tabs>

      <RetrievePasswordDialog
        result={revealFor}
        onOpenChange={(open) => { if (!open) setRevealFor(null); }}
        onGoToDomains={() => setTab('domains')}
      />
      {confirmDialog}
    </div>
  );
};

export default BreachMonitor;
